import React, { useCallback, useMemo, useRef } from 'react';
import { Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Section from '../../../layout/Section';
import Button from '../../../common/inputs/Button';
import EditableTable, { EditableTableColumn, EditableTableRef } from '../../../tables/common/EditableTable';
import { EditableTableConfig } from '../../../tables/common/types';
import { AdminAbzug } from '../../../../services/api/admin';
import { useAdminAbzuege } from '../hooks/useAdminAbzuege';
import { useToast } from '../../../../contexts/ToastContext';
import { formatNumber } from '../../../../utils/formatters';

interface AbzuegeSectionProps {
  orgId: string;
}

const AbzuegeSection: React.FC<AbzuegeSectionProps> = ({ orgId }) => {
  const { abzuege, loading, create, update, remove } = useAdminAbzuege(orgId);
  const { showError } = useToast();
  const tableRef = useRef<EditableTableRef>(null);

  const columns: EditableTableColumn[] = useMemo(() => [
    {
      key: 'bezeichnung',
      label: 'Bezeichnung',
      align: 'left' as const,
      editConfig: { type: 'text' as const, required: true },
    },
    {
      key: 'wert',
      label: 'Wert',
      align: 'right' as const,
      width: '160px',
      editConfig: { type: 'number' as const, suffix: '%', required: true, allowDecimals: true, min: 0, max: 100 },
    },
  ], []);

  const editConfig: EditableTableConfig<AdminAbzug> = useMemo(() => ({
    fields: {
      bezeichnung: { type: 'text', required: true },
      wert: { type: 'number', suffix: '%', required: true, allowDecimals: true, min: 0, max: 100 },
    },
    onCreate: async (data: Partial<AdminAbzug>) => {
      try {
        return await create({ bezeichnung: data.bezeichnung ?? '', wert: data.wert ?? 0 }) as unknown as AdminAbzug;
      } catch {
        showError('Abzugsart konnte nicht angelegt werden');
        throw new Error('create failed');
      }
    },
    onUpdate: async (id: string, changes: Partial<AdminAbzug>) => {
      try {
        return await update(id, { bezeichnung: changes.bezeichnung, wert: changes.wert });
      } catch {
        showError('Abzugsart konnte nicht aktualisiert werden');
        throw new Error('update failed');
      }
    },
    onDelete: async (id: string) => {
      try {
        await remove(id);
      } catch {
        showError('Abzugsart konnte nicht gelöscht werden');
        throw new Error('delete failed');
      }
    },
  }), [create, update, remove, showError]);

  const renderCell = useCallback((column: EditableTableColumn, _row: AdminAbzug, value: unknown) => {
    if (column.key === 'wert') {
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {formatNumber(value as number, 2)} %
        </Typography>
      );
    }
    return undefined;
  }, []);

  return (
    <Section
      title="Abzüge"
      noPadding
      actions={
        <Button variant="primary" onClick={() => tableRef.current?.startCreating({ bezeichnung: '', wert: 0 })}>
          <AddIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
          Abzugsart anlegen
        </Button>
      }
    >
      <EditableTable<AdminAbzug>
        ref={tableRef}
        columns={columns}
        data={abzuege}
        editConfig={editConfig}
        canEdit
        renderCell={renderCell}
        actionsColumnWidth="60px"
        actionsColumnAlign="right"
        showBorder={false}
        emptyMessage="Keine Abzugsarten konfiguriert"
      />
    </Section>
  );
};

export default AbzuegeSection;
